import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutTemplate } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useTemplate } from '../../hooks/useTemplate';
import { useInstantiateTemplate } from '../../hooks/useTemplateMutations';
import { TemplatePreviewDialog } from './TemplatePreviewDialog';
import type { TemplateSummary } from '../../types';

const MAX_CHIPS = 6;

interface TemplateGalleryRowProps {
  template: TemplateSummary;
  onClose: () => void;
}

/**
 * Field-label chips come from a per-row `GET /templates/{id}` fetch — the list
 * endpoint (`FormTemplateSummaryDto`) only has `fieldCount`, not the labels.
 * Acceptable N+1 given the gallery only ever shows a small, bounded set of
 * templates (same tradeoff already documented for `getSubmissionsForStats`).
 */
export function TemplateGalleryRow({ template, onClose }: TemplateGalleryRowProps) {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { template: detail } = useTemplate(template.id);
  const instantiate = useInstantiateTemplate(template.id);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fieldLabels =
    detail?.fields
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((f) => f.label) ?? [];
  const visibleLabels = fieldLabels.slice(0, MAX_CHIPS);
  const extraCount = fieldLabels.length - visibleLabels.length;

  async function handleUse() {
    try {
      const { data: created } = await instantiate.mutateAsync();
      onClose();
      navigate(`/${orgId}/forms/${created.id}`);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <div
      className="flex items-start justify-between gap-4 py-4"
      data-testid={`forms-template-gallery-row-${template.id}`}
    >
      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        className="flex min-w-0 items-start gap-3 text-left"
        data-testid={`forms-template-gallery-preview-trigger-${template.id}`}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-purple-100">
          <LayoutTemplate className="h-4 w-4 text-purple-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium hover:underline">{template.title}</p>
          {template.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {!detail ? (
              <Skeleton className="h-5 w-40" />
            ) : (
              <>
                {visibleLabels.map((label, i) => (
                  <Badge key={i} variant="outline" className="font-normal text-muted-foreground">
                    {label}
                  </Badge>
                ))}
                {extraCount > 0 && (
                  <Badge variant="outline" className="font-normal text-muted-foreground">
                    +{extraCount}
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>
      </button>

      <Button
        onClick={handleUse}
        disabled={instantiate.isPending}
        className="shrink-0"
        data-testid={`forms-template-gallery-use-${template.id}`}
      >
        {t('templates.gallery.useTemplate')}
      </Button>

      <TemplatePreviewDialog
        templateId={template.id}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        backLabel={t('templates.preview.backToGallery')}
        onUsed={onClose}
      />
    </div>
  );
}
